import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import DisplayDate from '../DisplayDate/DisplayDate';
import { GameSelection, getPrizeText, getSelections } from './HistoryItem.functions';
import { getAttributeValue } from '../../interfaces/sports';
import { addSuffix } from '../../common/ordinals';
import { Selection } from '../../interfaces/sports';
import { useGetOddsFromPrice } from '../../store/options/optionsStore';
import './HistoryItem.scss';
import { isAusGame } from '../../store/options/optionsStore';
import { Entry } from '../../interfaces/predictors';
import { useGetEntryEvents } from '../../store/history/historyActions';
import { getPrizeCodeClassModifier } from '../PrizePool/PrizePool.functions';
import LoadingIcon from 'theme/components/LoadingIcon/LoadingIcon';

const silkUrl = process.env.REACT_APP_SILK_URL || 'https://www.imagecache365.com/JockeySilksTransparent/';

interface MyHistoryItemProps {
  startOpen?: boolean;
  entry: Entry;
}

const HistoryItem = ({ startOpen, entry }: MyHistoryItemProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(!!startOpen);
  const { events, isFetching } = useGetEntryEvents(entry, open);

  const prize = entry?.prizes ? entry.prizes[0] : undefined;
  if (entry.startTime === undefined) return null;
  const onHeaderClick = () => setOpen(!open);
  const selections = getSelections(entry, events || []);
  const prizeCodeClassModifier = getPrizeCodeClassModifier({ prizeCode: prize?.prizeCode });

  return (
    <div className='historic-game'>
      <div className='historic-game__header' onClick={onHeaderClick}>
        <div className='historic-game__header-date'>
          <DisplayDate date={entry.startTime} format='historic-game' />
        </div>
        {prize || entry.returns ? (
          <div
            className={
              prizeCodeClassModifier
                ? `historic-game__header-prize historic-game__header-prize${prizeCodeClassModifier}`
                : 'historic-game__header-prize'
            }
          >
            {getPrizeText({
              t,
              prize: entry.returns,
              currencyCode: entry.currencyCode,
              prizeCode: prize?.prizeCode,
              prizeMultiplier: prize?.prizeMultiplier,
            })}
          </div>
        ) : null}
      </div>
      {open ? (
        <>
          {isFetching ? (
            <div className={'historic-game__loading'}>
              <LoadingIcon />
            </div>
          ) : (
            selections.map((selection, index) => <Fixture key={index} {...selection} />)
          )}
        </>
      ) : null}
    </div>
  );
};
export default HistoryItem;

interface FixtureRowProps {
  selection: Selection;
  replaced: boolean;
  countryCode?: string;
}

const FixtureRow = ({ selection, replaced, countryCode }: FixtureRowProps) => {
  const { t } = useTranslation();
  const getOddsFromPrice = useGetOddsFromPrice();
  const jockeyName = getAttributeValue({
    name: 'jockeyName',
    attributes: selection.attributes,
  });
  const trainerName = getAttributeValue({
    name: 'trainerName',
    attributes: selection.attributes,
  });
  const silkImage = getAttributeValue({
    name: 'silkImage',
    attributes: selection.attributes,
  });
  const place = typeof selection.place === 'number' ? addSuffix(selection.place) : selection.place ?? '-';
  const nonRunnerText = isAusGame(countryCode) ? 'scratched' : 'nr';
  const jerseyClass = isAusGame(countryCode) ? 'fixture-container__row__silk--au' : '';
  const resultText = replaced ? nonRunnerText : place;

  return (
    <div className={`fixture-container__row ${replaced ? 'replaced' : ''}`}>
      <div className={`fixture-container__row__silk ${jerseyClass}`}>
        <img
          src={`${silkUrl}${silkImage}`}
          alt={''}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.style.display = 'none';
          }}
        />
      </div>
      <div className={'fixture-container__row__details'}>
        <div className={`fixture-container__row__details__name ${replaced ? 'replaced' : ''}`}>
          {selection.selectionName}{' '}
          <span className={`fixture-container__row__details__odds ${replaced ? 'replaced' : ''}`}>
            {getOddsFromPrice(selection.price)}
          </span>
        </div>
        <div className={`fixture-container__row__attributes ${replaced ? 'replaced' : ''}`}>
          <div className={'fixture-container__row__details__jockey'}>{jockeyName}</div>
          <div className={'fixture-container__row__details__bullet-point'}>.</div>
          <div className={'fixture-container__row__details__trainer'}>{trainerName}</div>
        </div>
      </div>
      <div className={'fixture-container__row__result'}>{t(resultText)}</div>
    </div>
  );
};

const Fixture = ({ event, selection, replaced, countryCode }: GameSelection) => {
  const { t } = useTranslation();
  if (!event.eventActive) {
    return (
      <div className='fixture-container'>
        <div className={'fixture-container__event'}>{event.eventName}</div>
        <div className='fixture-container__row'>
          <div className='fixture-container__row__details__name'>{t('abandoned')}</div>
        </div>
      </div>
    );
  }

  if (replaced) replaced.price = { fractional: 'SP' };
  return (
    <div className={`fixture-container`}>
      <div className={'fixture-container__event'}>{event.eventName}</div>
      <FixtureRow selection={selection} replaced={!!replaced} countryCode={countryCode} />
      {replaced && <FixtureRow selection={replaced} replaced={false} countryCode={countryCode} />}
    </div>
  );
};
